import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { providerProfileApi, ApiError } from '../api';
import type { ProviderService, ServiceStatus, ServiceRequest } from '../types/provider';

export interface ServiceVariant {
	id?: string;
	size: string;
	length: string;
	duration: number;
	price: number;
}

export interface Service {
	id: string;
	name: string;
	description: string;
	status: ServiceStatus;
	variants: ServiceVariant[];
}

// Map API response → UI model
const mapApiService = (apiService: ProviderService): Service => ({
	id: String(apiService.id),
	name: apiService.serviceName,
	description: apiService.description,
	status: apiService.status,
	variants: apiService.pricingCombinations.map((combo, idx) => ({
		id: `${apiService.id}-${idx}`,
		size: combo.size,
		length: combo.length,
		duration: combo.durationMinutes,
		price: combo.price,
	})),
});

// Map UI model → API request body
const toServiceRequest = (service: Service): ServiceRequest => ({
	serviceName: service.name,
	description: service.description,
	pricingCombinations: service.variants.map(v => ({
		size: v.size,
		length: v.length,
		durationMinutes: v.duration,
		price: v.price,
	})),
	status: service.status,
});

interface ServicesState {
	services: Service[];
	isLoading: boolean;
	error: string | null;
	fetchServices: (locationId: number) => Promise<void>;
	addService: (locationId: number, service: Service) => Promise<void>;
	updateService: (locationId: number, id: string, service: Service) => Promise<void>;
	deleteService: (locationId: number, id: string) => Promise<void>;
	clearServices: () => void;
}

const useServicesStore = create<ServicesState>()(
	devtools(
		set => ({
			services: [],
			isLoading: false,
			error: null,

			fetchServices: async (locationId: number) => {
				set({ isLoading: true, error: null });

				try {
					const apiServices = await providerProfileApi.getServices(locationId);
					set({
						services: apiServices.map(mapApiService),
						isLoading: false,
					});
				} catch (error) {
					const apiError = error as ApiError;
					set({
						error: apiError.message || 'Failed to load services',
						isLoading: false,
					});

					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Services fetch error:', apiError);
					}
				}
			},

			addService: async (locationId: number, service: Service) => {
				const created = await providerProfileApi.createService(
					locationId,
					toServiceRequest(service)
				);
				set(state => ({
					services: [...state.services, mapApiService(created)],
				}));
			},

			updateService: async (locationId: number, id: string, service: Service) => {
				const updated = await providerProfileApi.updateService(
					Number(id),
					locationId,
					toServiceRequest(service)
				);
				set(state => ({
					services: state.services.map(s => (s.id === id ? mapApiService(updated) : s)),
				}));
			},

			deleteService: async (locationId: number, id: string) => {
				await providerProfileApi.deleteService(Number(id), locationId);
				set(state => ({
					services: state.services.filter(s => s.id !== id),
				}));
			},

			clearServices: () => {
				set({ services: [], isLoading: false, error: null });
			},
		}),
		{ name: 'services-store' }
	)
);

export default useServicesStore;
